import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { postAuthLogout, postAuthPasswordChange } from '@/api/auth';
import { useAuth } from '@/stores/auth';
import { homeForRole } from '../roles';
import { AuthShell, FormError, errText, inputCls } from '../shared';

/** Forced after sign-in when the account has a temporary password (`flags.must_change_password`). */
export function ChangePasswordPage() {
  const user = useAuth((s) => s.user);
  const nav = useNavigate();
  const [v, setV] = useState({ current_password: '', password: '', password_confirmation: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!user) return <Navigate to="/login" replace />;
  if (!user.mustChangePassword) return <Navigate to={homeForRole(user.role)} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (v.password !== v.password_confirmation) { setError('Passwords do not match.'); return; }
    setBusy(true); setError(null);
    try {
      await postAuthPasswordChange(v);
      useAuth.getState().patchUser({ mustChangePassword: false });
      nav(homeForRole(user.role), { replace: true });
    } catch (err) { setError(errText(err)); } finally { setBusy(false); }
  };
  const signOut = async () => { try { await postAuthLogout(); } catch { /* ignore */ } useAuth.getState().logout(); nav('/login', { replace: true }); };

  return (
    <AuthShell title="Choose a new password" subtitle="You signed in with a temporary password. Set your own password to continue.">
      <form onSubmit={submit} className="space-y-3">
        <input className={inputCls} type="password" autoComplete="current-password" placeholder="Temporary password" value={v.current_password} onChange={(e) => setV({ ...v, current_password: e.target.value })} required />
        <input className={inputCls} type="password" autoComplete="new-password" minLength={8} placeholder="New password (min 8)" value={v.password} onChange={(e) => setV({ ...v, password: e.target.value })} required />
        <input className={inputCls} type="password" autoComplete="new-password" placeholder="Confirm new password" value={v.password_confirmation} onChange={(e) => setV({ ...v, password_confirmation: e.target.value })} required />
        <FormError text={error} />
        <Button type="submit" className="w-full" disabled={busy || v.password.length < 8}>{busy ? 'Saving…' : 'Save and continue'}</Button>
        <Button type="button" variant="ghost" className="w-full" onClick={signOut}>Sign out</Button>
      </form>
    </AuthShell>
  );
}
